import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { cn } from "../lib/utils";
import { usePsychQuestions, useSavePsych } from "../hooks/useProfile";
import type { PsychDimension } from "@parble/types";

const CHUNK = 4;

export function OnboardingWerte() {
  const { data: questions = [], isLoading } = usePsychQuestions();
  const save = useSavePsych();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [slide, setSlide] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const slides = useMemo(() => {
    const arr: (typeof questions)[] = [];
    for (let i = 0; i < questions.length; i += CHUNK) {
      arr.push(questions.slice(i, i + CHUNK));
    }
    return arr;
  }, [questions]);

  if (isLoading) {
    return (
      <AppShell hideNav>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      </AppShell>
    );
  }

  const current = slides[slide] ?? [];
  const totalSlides = slides.length;
  const answered = Object.keys(answers).length;
  const slideComplete = current.every(
    (q) => answers[q.dimension] !== undefined,
  );
  const isLast = slide === totalSlides - 1;

  async function handleFinish() {
    setError(null);
    try {
      await save.mutateAsync({
        answers: Object.entries(answers).map(([dimension, value]) => ({
          dimension: dimension as PsychDimension,
          value,
        })),
      });
      navigate("/onboarding/suche");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    }
  }

  return (
    <AppShell hideNav>
      <section className="container max-w-2xl py-10">
        <div className="mb-8 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
            <Brain className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-500">
              Schritt 3 von 4
            </p>
            <h1 className="text-2xl font-bold tracking-tight">Was ist dir wichtig?</h1>
          </div>
        </div>

        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-surface">
          <div
            className="h-full bg-brand-gradient transition-all"
            style={{ width: `${((slide + 1) / totalSlides) * 100}%` }}
          />
        </div>

        <div className="card p-6 md:p-8">
          <p className="text-[15px] text-ink-500">
            Bewerte, wie wichtig dir diese Lebensbereiche sind. 1 = gar nicht
            wichtig, 10 = extrem wichtig.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700"
            >
              {error}
            </div>
          )}

          <div className="mt-8 space-y-7">
            {current.map((q) => {
              const value = answers[q.dimension];
              return (
                <div key={q.dimension}>
                  <div className="mb-3 flex items-baseline justify-between gap-4">
                    <label className="text-[15px] font-semibold text-ink-900">
                      {q.question}
                    </label>
                    <span
                      className={cn(
                        "text-sm font-bold tabular-nums",
                        value === undefined ? "text-ink-300" : "text-brand-500",
                      )}
                    >
                      {value ?? "—"} / 10
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={1}
                    value={value ?? 5}
                    onChange={(e) =>
                      setAnswers((a) => ({
                        ...a,
                        [q.dimension]: Number(e.target.value),
                      }))
                    }
                    className="w-full accent-brand-500"
                  />
                  <div className="mt-1 flex justify-between text-[10px] font-medium uppercase tracking-widest text-ink-300">
                    <span>egal</span>
                    <span>enorm wichtig</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setSlide((s) => Math.max(0, s - 1))}
              disabled={slide === 0}
              className="btn-secondary disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={handleFinish}
                disabled={!slideComplete || save.isPending}
                className="btn-primary disabled:opacity-50"
              >
                {save.isPending ? "Speichern …" : "Weiter"}
                {!save.isPending && <ArrowRight className="h-4 w-4" />}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setSlide((s) => Math.min(totalSlides - 1, s + 1))}
                disabled={!slideComplete}
                className="btn-primary disabled:opacity-50"
              >
                Weiter
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink-500">
          {answered} von {questions.length} beantwortet
        </p>
      </section>
    </AppShell>
  );
}
